import React, { FC, useCallback, useMemo, useRef } from 'react';
import { Link as RouterLink } from 'react-router';

import { dayjs } from '@tsl-frontend/tsl-utilities';

import {
    Box,
    Chip,
    Grid,
    GridColDef,
    GridPaginationModel,
    GridSortModel,
    IconButton,
    Icons,
    Stack,
    TSLLink,
    Typography,
} from '@thirdspacelearning/library';

import { Country, LearningObjective } from '@types';
import { CMSDataGrid } from '@shared';

interface Props {
    learningObjectives: LearningObjective[];
    total: number;
    loading: boolean;
    paginationModel: GridPaginationModel;
    onPaginationModelChange: (paginationModel: GridPaginationModel) => void;
    onSortModelChange: (sortModel: GridSortModel) => void;
    onEditObjective: (lo: LearningObjective) => void;
    country: Country;
}

const sx = {
    loTitleCell: {
        whiteSpace: 'normal',
    },
    compactChip: {
        mb: '2px',
        mr: 1,
    },
    activeIcon: {
        color: 'success.main',
    },
    inactiveIcon: {
        color: 'error.main',
    },
    standardCodeRow: {
        flexWrap: 'wrap',
    },
    learningContentCell: {
        alignItems: 'center',
        width: '100%',
    },
};

const LearningObjectivesTable: FC<Props> = ({
    learningObjectives,
    total,
    loading,
    paginationModel,
    onPaginationModelChange,
    onSortModelChange,
    onEditObjective,
    country,
}) => {
    const onEditObjectiveRef = useRef(onEditObjective);
    onEditObjectiveRef.current = onEditObjective;

    const handleEditObjective = useCallback((objective: LearningObjective) => {
        onEditObjectiveRef.current(objective);
    }, []);

    const columns = useMemo(
        (): GridColDef<LearningObjective>[] => [
            {
                field: 'id',
                type: 'string',
                headerName: 'ID',
                headerAlign: 'left',
                align: 'left',
                sortable: true,
                flex: 0,
                width: 70,
            },
            {
                field: 'title',
                type: 'string',
                headerName: 'TSL Learning Objective',
                headerAlign: 'left',
                align: 'left',
                sortable: true,
                renderCell: ({ row }) => (
                    <Box sx={sx.loTitleCell}>
                        {row.ai && (
                            <Chip
                                label={'AI'}
                                color="info"
                                size="small"
                                sx={sx.compactChip}
                                data-testid="ai-chip"
                            />
                        )}
                        {row.title}
                    </Box>
                ),
                flex: 3,
                minWidth: 250,
            },
            {
                field: 'active',
                type: 'string',
                headerName: 'Active',
                headerAlign: 'left',
                align: 'left',
                sortable: true,
                renderCell: (params) =>
                    params.row.active ? (
                        <Icons.Check sx={sx.activeIcon} data-testid="active" />
                    ) : (
                        <Icons.Clear
                            sx={sx.inactiveIcon}
                            data-testid="inactive"
                        />
                    ),
                maxWidth: 100,
            },
            {
                field: 'year_id',
                type: 'string',
                headerName: country === Country.GB ? 'Year Group' : 'Grade',
                headerAlign: 'left',
                align: 'left',
                sortable: true,
                flex: 1,
                maxWidth: 150,
            },
            {
                field: 'standard_codes',
                type: 'string',
                headerName: 'Standard Code',
                headerAlign: 'left',
                align: 'left',
                renderCell: ({ row }) =>
                    'standard_codes' in row && (
                        <Stack direction="row" sx={sx.standardCodeRow}>
                            {row.standard_codes.map((standardCode, idx) => (
                                <Chip
                                    key={idx}
                                    label={standardCode}
                                    variant="outlined"
                                    size="small"
                                    sx={sx.compactChip}
                                    data-testid="standard-code-chip"
                                />
                            ))}
                        </Stack>
                    ),
                flex: 1,
            },
            {
                field: 'learning_content',
                headerName: 'Learning Content',
                headerAlign: 'left',
                align: 'left',
                sortable: true,
                renderCell: ({ row }) => (
                    <Grid container sx={sx.learningContentCell}>
                        <Grid size={7} data-testid="slides-count">
                            {`${row.slides_counter} Slides`}
                        </Grid>
                        <TSLLink
                            component={RouterLink}
                            to={`/learning-content/${row.id}`}
                            color="primary"
                            data-testid="slides-link"
                        >
                            {row.slides_counter ? 'Edit' : 'Add'}
                        </TSLLink>
                    </Grid>
                ),
                maxWidth: 200,
                flex: 1,
            },
            {
                field: 'created_at',
                type: 'string',
                headerName: 'Created date',
                headerAlign: 'left',
                align: 'left',
                sortable: true,
                valueGetter: (value) =>
                    value && dayjs(value).format('DD/MM/YY HH:mm'),
                renderCell: ({ row }) => (
                    <Typography variant="caption" data-testid="created_at">
                        {row.created_at &&
                            dayjs(row.created_at).format('DD/MM/YY HH:mm')}
                    </Typography>
                ),
                flex: 1,
                maxWidth: 200,
            },
            {
                field: 'updated_at',
                type: 'string',
                headerName: 'Last modified date',
                headerAlign: 'left',
                align: 'left',
                sortable: true,
                valueGetter: (value) =>
                    value && dayjs(value).format('DD/MM/YY HH:mm'),
                renderCell: ({ row }) => (
                    <Typography variant="caption" data-testid="updated_at">
                        {row.updated_at &&
                            dayjs(row.updated_at).format('DD/MM/YY HH:mm')}
                    </Typography>
                ),
                flex: 1,
                maxWidth: 250,
            },
            {
                field: 'actions',
                type: 'string',
                display: 'flex',
                headerName: '',
                renderCell: ({ row }) => (
                    <IconButton
                        aria-label="edit"
                        size="small"
                        onClick={() => handleEditObjective(row)}
                        data-testid="slides-edit-button"
                    >
                        <Icons.Edit color="primary" />
                    </IconButton>
                ),
                sortable: false,
                width: 52,
            },
        ],
        [country, handleEditObjective],
    );

    const columnVisibilityModel = useMemo(
        () => ({
            standard_codes: country === Country.US,
        }),
        [country],
    );

    const loadingOverlaySlotProps = useMemo(
        () => ({
            loadingOverlay: { variant: 'linear-progress' as const },
        }),
        [],
    );

    const localeText = useMemo(
        () => ({
            noRowsLabel:
                "We're sorry, we couldn't find any learning objectives with those filters",
        }),
        [],
    );

    return (
        <CMSDataGrid
            columns={columns}
            rows={learningObjectives}
            rowCount={total}
            paginationMode="server"
            pageSizeOptions={[10, 25, 50]}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            columnVisibilityModel={columnVisibilityModel}
            filterMode="server"
            sortingMode="server"
            onSortModelChange={onSortModelChange}
            loading={loading}
            slotProps={loadingOverlaySlotProps}
            localeText={localeText}
            disableColumnMenu
            disableColumnSelector
            disableColumnFilter
            disableDensitySelector
            disableRowSelectionOnClick
            disableColumnResize
        />
    );
};

export default LearningObjectivesTable;
